/**
 * R-80 — Cross-report recommendation graph builder.
 * Clusters actions across reports, infers dependencies, builds a DAG roadmap.
 */
import { Type } from '@google/genai';
import type { SiteRagReport } from '../../types/siteRag';
import type {
  DependencyEdgeType,
  GraphReconciliationFinding,
  RecommendationEdge,
  RecommendationGraph,
  RecommendationNode,
  RecommendationPhase,
  RecommendationSourceRef,
} from '../../types/recommendationGraph';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import { GEMINI_PRO_MODEL } from '../../config/geminiModels';
import { cosineSimilarity } from '../embeddings/cosine';
import { embedText } from '../embeddings/embedText';
import {
  clusterActions,
  extractActions,
  type ActionEmbedder,
  type ExtractedAction,
  type ExtractActionsOptions,
} from './actionDeduper';

export type {
  RecommendationEdge,
  RecommendationGraph,
  RecommendationNode,
  GraphReconciliationFinding,
} from '../../types/recommendationGraph';

export type LlmDepInferFn = (
  prerequisite: RecommendationNode,
  dependent: RecommendationNode,
) => Promise<RecommendationEdge | null>;

interface TaggedAction extends ExtractedAction {
  reportId: string;
  reportType: string;
}

const EXPLICIT_AFTER_RE = /\b(?:after|following|once|upon completion of|depends on)\s+(.{8,120})/i;
const IMMEDIATE_RE = /\b(immediate|asap|urgent|now)\b/i;

const LLM_PAIR_CACHE = new Map<string, RecommendationEdge | null>();
const JUDGE_MODEL = GEMINI_PRO_MODEL;

// C10 — embedding rate-limit storm guard. Embedding many canonical-form
// strings via `Promise.all(items.map(embedder))` fans out an unbounded burst
// of concurrent embedContent calls, which trips the provider's per-minute
// quota (429) on large report sets. Cap concurrency so the embedder never
// issues more than EMBED_CONCURRENCY in-flight requests at once.
const EMBED_CONCURRENCY = 4;

/**
 * Map `items` through async `fn` with at most `limit` concurrent invocations.
 * Preserves input order in the output array (result[i] corresponds to
 * items[i]). A bounded replacement for `await Promise.all(items.map(fn))` that
 * keeps embedding bursts under the provider rate limit.
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const safeLimit = Math.max(1, Math.floor(limit));
  const results = new Array<R>(items.length);
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await fn(items[index], index);
    }
  }

  const workerCount = Math.min(safeLimit, items.length);
  const workers: Promise<void>[] = [];
  for (let i = 0; i < workerCount; i += 1) workers.push(worker());
  await Promise.all(workers);
  return results;
}

/**
 * C10 — wrap an injected embedder with an in-memory Map cache keyed by the
 * exact input string. The same canonicalForm is embedded across multiple
 * stages (explicit-edge inference, semantic-edge inference, append dedupe);
 * caching collapses those into a single underlying embedContent call per
 * distinct string for the lifetime of one graph build.
 */
function memoizeEmbedder(embedder: ActionEmbedder): ActionEmbedder {
  const cache = new Map<string, Promise<number[]>>();
  return (text: string): Promise<number[]> => {
    const cached = cache.get(text);
    if (cached) return cached;
    const pending = embedder(text);
    cache.set(text, pending);
    return pending;
  };
}

// R-131-ROADMAP-FIX-A
const PHASE_ORDER: RecommendationPhase[] = [
  'immediate', '30d', '90d', '6mo', '12mo+', 'unscheduled',
];

function hashPair(a: string, b: string): string {
  return [a, b].sort().join('|');
}

// R-ROADMAP-INC — content-stable node identity.
//
// Re-extraction renumbers cluster ids (`cluster-0`, `cluster-1`, …) because
// they depend on iteration order over whatever report set was passed in. To
// keep node identity stable across incremental updates we derive a key from
// the canonical recommendation text alone. Two clusters whose canonical text
// normalises identically share a stableKey and are treated as the same node.

/** Normalise canonical text for content-stable hashing: lowercase, collapse
 *  whitespace, strip trailing punctuation and bracketed citation markers. */
export function normalizeCanonicalText(text: string): string {
  return text
    .toLowerCase()
    .replace(/\[ref\s+\d+\]/gi, ' ')
    .replace(/[\s ]+/g, ' ')
    .trim()
    .replace(/[.;,:]+$/g, '');
}

/** Deterministic djb2-derived 32-bit hex hash of normalised canonical text. */
export function computeStableKey(canonicalText: string): string {
  const normalized = normalizeCanonicalText(canonicalText);
  let hash = 5381;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = ((hash << 5) + hash + normalized.charCodeAt(i)) | 0;
  }
  // Unsigned hex, zero-padded for stable id formatting.
  return `sk-${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

/** Backfill stableKey for nodes persisted before the field existed. */
export function ensureStableKey(node: RecommendationNode): RecommendationNode {
  if (node.stableKey) return node;
  return { ...node, stableKey: computeStableKey(node.canonicalForm) };
}

const PHASE_BUCKET_TOKENS = ['immediate', '30d', '90d', '6mo', '12mo+'] as const;

function parseDeadlineDays(deadline: string | undefined, refMs: number): number | null {
  if (!deadline?.trim()) return null;
  const raw = deadline.trim().toLowerCase();
  if (raw === 'immediate' || IMMEDIATE_RE.test(raw)) return 0;
  if (raw === '30d') return 30;
  if (raw === '90d') return 90;
  if (raw === '6mo') return 183;
  if (raw === '12mo+') return 400;

  const rel = raw.match(/(\d+)\s*(day|week|month|year|mo|yr)s?/);
  if (rel) {
    const n = parseInt(rel[1], 10);
    const unit = rel[2];
    if (unit.startsWith('day')) return n;
    if (unit.startsWith('week')) return n * 7;
    if (unit.startsWith('month') || unit === 'mo') return n * 30;
    if (unit.startsWith('year') || unit === 'yr') return n * 365;
  }

  const parsed = Date.parse(deadline);
  if (Number.isFinite(parsed)) {
    return Math.max(0, Math.ceil((parsed - refMs) / (24 * 60 * 60 * 1000)));
  }
  return null;
}

// R-131-ROADMAP-FIX-A — prefer extractor suggestedPhase; else unscheduled (not 12mo+)
export function bucketPhase(
  deadline: string | undefined,
  refDate = new Date(),
  withSuggestion?: RecommendationPhase,
): RecommendationPhase {
  if (!deadline?.trim()) {
    if (withSuggestion && withSuggestion !== 'unscheduled') return withSuggestion;
    return 'unscheduled';
  }
  const raw = deadline.trim().toLowerCase();
  if ((PHASE_BUCKET_TOKENS as readonly string[]).includes(raw)) {
    return raw as RecommendationPhase;
  }
  const days = parseDeadlineDays(deadline, refDate.getTime());
  if (days == null) {
    if (withSuggestion && withSuggestion !== 'unscheduled') return withSuggestion;
    return 'unscheduled';
  }
  const buckets = AUDIT_CONFIG.PHASE_BUCKET_DAYS ?? {
    immediate: 7,
    '30d': 30,
    '90d': 90,
    '6mo': 183,
  };
  if (days <= buckets.immediate) return 'immediate';
  if (days <= buckets['30d']) return '30d';
  if (days <= buckets['90d']) return '90d';
  if (days <= buckets['6mo']) return '6mo';
  return '12mo+';
}

function phaseRank(phase: RecommendationPhase): number {
  return PHASE_ORDER.indexOf(phase);
}

function clusterToNode(
  clusterId: string,
  canonical: ExtractedAction,
  members: TaggedAction[],
): RecommendationNode {
  const sourceReports: RecommendationSourceRef[] = [];
  const seen = new Set<string>();
  for (const m of members) {
    const key = `${m.reportId}:${m.sectionId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    sourceReports.push({
      reportId: m.reportId,
      reportType: m.reportType,
      sectionId: m.sectionId,
    });
  }
  return {
    id: clusterId,
    stableKey: computeStableKey(canonical.text),
    canonicalForm: canonical.text,
    text: canonical.text,
    owner: canonical.owner,
    deadline: canonical.deadline,
    phase: bucketPhase(canonical.deadline, new Date(), canonical.suggestedPhase),
    status: 'not-started',
    sourceReports,
    clusterId,
    memberCount: members.length,
  };
}

/** Extract + cross-report cluster recommendations into canonical nodes. */
export async function extractRecommendations(
  reports: SiteRagReport[],
  embedder: ActionEmbedder,
  threshold = AUDIT_CONFIG.RECOMMENDATION_CLUSTER_THRESHOLD,
  extractOpts?: ExtractActionsOptions,
): Promise<RecommendationNode[]> {
  const tagged: TaggedAction[] = [];
  for (const [reportIndex, report] of reports.entries()) {
    const reportId = report.id ?? 'unknown';
    const reportType = report.reportType ?? 'feasibility';
    for (const action of await extractActions(report, extractOpts)) {
      // Local section#row IDs repeat across reports. Qualify them before
      // clustering so vector lookups and source links retain the right report.
      tagged.push({ ...action, id: `${reportId}:${reportIndex}:${action.id}`, reportId, reportType });
    }
  }
  if (tagged.length === 0) return [];

  const clusters = await clusterActions(tagged, embedder, threshold, { crossReport: true });
  return clusters.map((c) => {
    const members = c.members.map((m) => {
      const t = tagged.find((a) => a.id === m.id);
      return t ?? ({ ...m, reportId: 'unknown', reportType: 'feasibility' } as TaggedAction);
    });
    return clusterToNode(c.clusterId, c.canonical, members);
  });
}

function edgeId(source: string, target: string, type: DependencyEdgeType): string {
  return `${source}->${target}:${type}`;
}

// R-131-ROADMAP-FIX-B — deadline edges when parseable; phase-only fallback otherwise
function inferTemporalEdges(nodes: RecommendationNode[]): RecommendationEdge[] {
  const edges: RecommendationEdge[] = [];
  const refMs = Date.now();
  const sorted = [...nodes].sort((a, b) => {
    const da = parseDeadlineDays(a.deadline, refMs);
    const db = parseDeadlineDays(b.deadline, refMs);
    if (da != null && db != null && da !== db) return da - db;
    return phaseRank(a.phase) - phaseRank(b.phase);
  });

  for (let i = 0; i < sorted.length - 1; i += 1) {
    const src = sorted[i];
    const tgt = sorted[i + 1];
    if (src.id === tgt.id) continue;
    const srcDays = parseDeadlineDays(src.deadline, refMs);
    const tgtDays = parseDeadlineDays(tgt.deadline, refMs);
    if (srcDays != null && tgtDays != null && srcDays < tgtDays) {
      edges.push({
        id: edgeId(src.id, tgt.id, 'temporal'),
        source: src.id,
        target: tgt.id,
        type: 'temporal',
        confidence: 0.65,
        reason: `Earlier deadline (${src.deadline}) precedes ${tgt.deadline}`,
      });
      continue;
    }
    if (phaseRank(src.phase) < phaseRank(tgt.phase)) {
      edges.push({
        id: edgeId(src.id, tgt.id, 'temporal'),
        source: src.id,
        target: tgt.id,
        type: 'temporal',
        confidence: 0.4,
        reason: 'phase-only temporal ordering',
      });
    }
  }
  return edges;
}

// R-131-ROADMAP-FIX-B — weak co-location edges within the same report section
function inferSemanticClusterEdges(nodes: RecommendationNode[]): RecommendationEdge[] {
  const edges: RecommendationEdge[] = [];
  const bySection = new Map<string, RecommendationNode[]>();

  for (const node of nodes) {
    for (const ref of node.sourceReports) {
      const key = `${ref.reportId}:${ref.sectionId}`;
      const list = bySection.get(key) ?? [];
      if (!list.some((n) => n.id === node.id)) list.push(node);
      bySection.set(key, list);
    }
  }

  for (const group of bySection.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => a.id.localeCompare(b.id));
    for (let i = 0; i < sorted.length - 1; i += 1) {
      const src = sorted[i];
      const tgt = sorted[i + 1];
      if (src.id === tgt.id) continue;
      edges.push({
        id: edgeId(src.id, tgt.id, 'cluster'),
        source: src.id,
        target: tgt.id,
        type: 'cluster',
        confidence: 0.3,
        reason: 'Co-located in same report section',
      });
    }
  }
  return edges;
}

function inferActorEdges(nodes: RecommendationNode[]): RecommendationEdge[] {
  const edges: RecommendationEdge[] = [];
  const byOwner = new Map<string, RecommendationNode[]>();
  for (const n of nodes) {
    const owner = n.owner?.trim().toLowerCase();
    if (!owner) continue;
    const list = byOwner.get(owner) ?? [];
    list.push(n);
    byOwner.set(owner, list);
  }

  for (const group of byOwner.values()) {
    if (group.length < 2) continue;
    const sorted = [...group].sort((a, b) => phaseRank(a.phase) - phaseRank(b.phase));
    for (let i = 0; i < sorted.length - 1; i += 1) {
      const src = sorted[i];
      const tgt = sorted[i + 1];
      if (src.id === tgt.id) continue;
      edges.push({
        id: edgeId(src.id, tgt.id, 'actor'),
        source: src.id,
        target: tgt.id,
        type: 'actor',
        confidence: 0.55,
        reason: `Shared owner ${src.owner}`,
      });
    }
  }
  return edges;
}

async function inferExplicitEdges(
  nodes: RecommendationNode[],
  embedder: ActionEmbedder,
  threshold = AUDIT_CONFIG.RECOMMENDATION_CLUSTER_THRESHOLD ?? 0.8,
): Promise<RecommendationEdge[]> {
  const edges: RecommendationEdge[] = [];
  const vectors = await mapWithConcurrency(nodes, EMBED_CONCURRENCY, (n) => embedder(n.canonicalForm));
  const byText = new Map<string, RecommendationNode>();
  for (const node of nodes) {
    byText.set(node.canonicalForm.trim().toLowerCase(), node);
  }

  for (const node of nodes) {
    const match = node.text.match(EXPLICIT_AFTER_RE);
    if (!match) continue;
    const phrase = match[1].replace(/[.[\]]+$/, '').trim();
    if (!phrase) continue;

    const exact = byText.get(phrase.toLowerCase());
    if (exact && exact.id !== node.id) {
      edges.push({
        id: edgeId(exact.id, node.id, 'explicit'),
        source: exact.id,
        target: node.id,
        type: 'explicit',
        confidence: 0.95,
        reason: `Explicit prerequisite phrase "${phrase.slice(0, 60)}"`,
      });
      continue;
    }

    const phraseVec = await embedder(phrase);

    let best: { node: RecommendationNode; sim: number } | null = null;
    for (let i = 0; i < nodes.length; i += 1) {
      const candidate = nodes[i];
      if (candidate.id === node.id) continue;
      const sim = cosineSimilarity(phraseVec, vectors[i]);
      if (sim >= threshold && (!best || sim > best.sim)) {
        best = { node: candidate, sim };
      }
    }
    if (!best) continue;
    edges.push({
      id: edgeId(best.node.id, node.id, 'explicit'),
      source: best.node.id,
      target: node.id,
      type: 'explicit',
      confidence: Math.min(0.95, best.sim),
      reason: `Explicit prerequisite phrase "${phrase.slice(0, 60)}"`,
    });
  }
  return edges;
}

function buildLlmPrompt(a: RecommendationNode, b: RecommendationNode): string {
  return [
    'You infer prerequisite dependencies between site-development recommendations.',
    'Return JSON only. If A must complete before B, set depends=true with prerequisiteId and dependentId.',
    '',
    `Node A (${a.id}): ${a.canonicalForm}`,
    `Node B (${b.id}): ${b.canonicalForm}`,
    '',
    'Rules:',
    '- depends=true only when one clearly blocks the other',
    '- prerequisiteId is the node that must happen first',
    '- dependentId is the node that waits on the prerequisite',
    '- confidence 0.5-0.95',
  ].join('\n');
}

function parseLlmDepResponse(
  text: string | undefined | null,
  a: RecommendationNode,
  b: RecommendationNode,
): RecommendationEdge | null {
  if (!text?.trim()) return null;
  try {
    const parsed = JSON.parse(text) as {
      depends?: boolean;
      prerequisiteId?: string;
      dependentId?: string;
      confidence?: number;
      reason?: string;
    };
    if (!parsed.depends) return null;
    const prereq = parsed.prerequisiteId === a.id ? a : parsed.prerequisiteId === b.id ? b : null;
    const dep = parsed.dependentId === a.id ? a : parsed.dependentId === b.id ? b : null;
    if (!prereq || !dep || prereq.id === dep.id) return null;
    const confidence = typeof parsed.confidence === 'number'
      ? Math.max(0.5, Math.min(0.95, parsed.confidence))
      : 0.7;
    return {
      id: edgeId(prereq.id, dep.id, 'semantic'),
      source: prereq.id,
      target: dep.id,
      type: 'semantic',
      confidence,
      reason: parsed.reason ?? 'LLM semantic dependency',
    };
  } catch {
    return null;
  }
}

/** Default Gemini dependency inference — overridable in tests. */
export const defaultLlmInferDeps: LlmDepInferFn = async (prerequisite, dependent) => {
  const key = hashPair(prerequisite.id, dependent.id);
  if (LLM_PAIR_CACHE.has(key)) return LLM_PAIR_CACHE.get(key) ?? null;

  try {
    const { ai } = await import('../../services/geminiClient');
    const response = await ai.models.generateContent({
      model: JUDGE_MODEL,
      contents: buildLlmPrompt(prerequisite, dependent),
      config: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            depends: { type: Type.BOOLEAN },
            prerequisiteId: { type: Type.STRING },
            dependentId: { type: Type.STRING },
            confidence: { type: Type.NUMBER },
            reason: { type: Type.STRING },
          },
          required: ['depends'],
        },
      },
    });
    const edge = parseLlmDepResponse(response.text, prerequisite, dependent);
    LLM_PAIR_CACHE.set(key, edge);
    return edge;
  } catch {
    LLM_PAIR_CACHE.set(key, null);
    return null;
  }
};

async function inferSemanticEdges(
  nodes: RecommendationNode[],
  embedder: ActionEmbedder,
  llmInferDeps?: LlmDepInferFn,
): Promise<RecommendationEdge[]> {
  if (!llmInferDeps || nodes.length < 2) return [];
  const vectors = await mapWithConcurrency(nodes, EMBED_CONCURRENCY, (n) => embedder(n.canonicalForm));
  const pairs: Array<{ a: RecommendationNode; b: RecommendationNode; sim: number }> = [];

  for (let i = 0; i < nodes.length; i += 1) {
    for (let j = i + 1; j < nodes.length; j += 1) {
      const sim = cosineSimilarity(vectors[i], vectors[j]);
      pairs.push({ a: nodes[i], b: nodes[j], sim });
    }
  }

  pairs.sort((x, y) => y.sim - x.sim);
  const cap = AUDIT_CONFIG.GRAPH_LLM_MAX_PAIRS ?? 100;
  const edges: RecommendationEdge[] = [];

  for (const { a, b } of pairs.slice(0, cap)) {
    const edge = await llmInferDeps(a, b);
    if (edge) edges.push(edge);
  }
  return edges;
}

/** R-131-ROADMAP-FIX-B — highest-priority nodes when subsetting for LLM dep inference. */
function selectLlmInferenceNodes(nodes: RecommendationNode[]): RecommendationNode[] {
  const cap = AUDIT_CONFIG.GRAPH_LLM_MAX_NODES ?? 20;
  if (nodes.length <= cap) return nodes;

  const statusPriority: Record<string, number> = {
    initiated: 4,
    'pending-feedback': 4,
    blocked: 3,
    'not-started': 2,
    completed: 1,
    'in-progress': 4,
    complete: 1,
  };

  return [...nodes]
    .sort((a, b) => {
      const phaseDelta = phaseRank(a.phase) - phaseRank(b.phase);
      if (phaseDelta !== 0) return phaseDelta;
      const statusDelta = (statusPriority[b.status] ?? 0) - (statusPriority[a.status] ?? 0);
      if (statusDelta !== 0) return statusDelta;
      return a.id.localeCompare(b.id);
    })
    .slice(0, cap);
}

function resolveLlmInferDeps(
  nodes: RecommendationNode[],
  opts: { llmInferDeps?: LlmDepInferFn | null; skipSemantic?: boolean },
): LlmDepInferFn | null {
  if (opts.skipSemantic) return null;
  if (opts.llmInferDeps !== undefined) return opts.llmInferDeps;
  if (!AUDIT_CONFIG.ENABLE_LLM_DEP_INFERENCE) return null;
  if (nodes.length > (AUDIT_CONFIG.GRAPH_LLM_MAX_NODES ?? 20)) return null;
  return defaultLlmInferDeps;
}

/** Infer dependency edges from semantic, temporal, actor, explicit, and cluster signals. */
export async function inferDependencies(
  nodes: RecommendationNode[],
  embedder: ActionEmbedder,
  opts: {
    llmInferDeps?: LlmDepInferFn | null;
    skipSemantic?: boolean;
  } = {},
): Promise<RecommendationEdge[]> {
  if (nodes.length === 0) return [];
  const temporal = inferTemporalEdges(nodes);
  const actor = inferActorEdges(nodes);
  const cluster = inferSemanticClusterEdges(nodes);
  const explicit = await inferExplicitEdges(nodes, embedder);
  const llmInferDeps = resolveLlmInferDeps(nodes, opts);
  const semantic = llmInferDeps == null
    ? []
    : await inferSemanticEdges(selectLlmInferenceNodes(nodes), embedder, llmInferDeps);
  return dedupeEdges([...temporal, ...actor, ...explicit, ...cluster, ...semantic]);
}

function dedupeEdges(edges: RecommendationEdge[]): RecommendationEdge[] {
  const byKey = new Map<string, RecommendationEdge>();
  for (const e of edges) {
    const key = `${e.source}->${e.target}`;
    const existing = byKey.get(key);
    if (!existing || e.confidence > existing.confidence) {
      byKey.set(key, e);
    }
  }
  return [...byKey.values()];
}

function findCycle(edges: RecommendationEdge[], nodeIds: Set<string>): string[] | null {
  const adj = new Map<string, string[]>();
  for (const id of nodeIds) adj.set(id, []);
  for (const e of edges) {
    if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) continue;
    adj.get(e.source)?.push(e.target);
  }

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const stack: string[] = [];

  function dfs(node: string): string[] | null {
    visiting.add(node);
    stack.push(node);
    for (const next of adj.get(node) ?? []) {
      if (visited.has(next)) continue;
      if (visiting.has(next)) {
        const idx = stack.indexOf(next);
        return stack.slice(idx).concat(next);
      }
      const cycle = dfs(next);
      if (cycle) return cycle;
    }
    stack.pop();
    visiting.delete(node);
    visited.add(node);
    return null;
  }

  for (const id of nodeIds) {
    if (!visited.has(id)) {
      const cycle = dfs(id);
      if (cycle) return cycle;
    }
  }
  return null;
}

/** Merge nodes + edges into a DAG; break cycles on lowest-confidence edge. */
export function buildRecommendationGraph(
  nodes: RecommendationNode[],
  edges: RecommendationEdge[],
): { nodes: RecommendationNode[]; edges: RecommendationEdge[]; findings: GraphReconciliationFinding[] } {
  const nodeIds = new Set(nodes.map((n) => n.id));
  let workingEdges = edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
  const findings: GraphReconciliationFinding[] = [];

  for (let guard = 0; guard < 20; guard += 1) {
    const cycle = findCycle(workingEdges, nodeIds);
    if (!cycle || cycle.length < 2) break;
    const cycleSet = new Set(cycle);
    const cycleEdges = workingEdges.filter((e) => cycleSet.has(e.source) && cycleSet.has(e.target));
    if (cycleEdges.length === 0) break;
    const weakest = [...cycleEdges].sort((a, b) => a.confidence - b.confidence)[0];
    workingEdges = workingEdges.filter((e) => e.id !== weakest.id);
    findings.push({
      kind: 'CYCLE_BROKEN',
      severity: 'warn',
      message: `Removed lowest-confidence edge in cycle: ${weakest.source} → ${weakest.target}`,
      edgeIds: [weakest.id],
      nodeIds: cycle.filter((id, i, arr) => arr.indexOf(id) === i),
    });
  }

  const prereqMap = new Map<string, string[]>();
  const downstreamMap = new Map<string, number>();
  for (const id of nodeIds) {
    prereqMap.set(id, []);
    downstreamMap.set(id, 0);
  }
  for (const e of workingEdges) {
    prereqMap.get(e.target)?.push(e.source);
    downstreamMap.set(e.source, (downstreamMap.get(e.source) ?? 0) + 1);
  }

  const enrichedNodes = nodes.map((n) => ({
    ...n,
    prerequisites: prereqMap.get(n.id) ?? [],
    downstreamCount: downstreamMap.get(n.id) ?? 0,
  }));

  return { nodes: enrichedNodes, edges: workingEdges, findings };
}

function longestPathLength(nodeIds: Set<string>, edges: RecommendationEdge[]): number {
  const adj = new Map<string, string[]>();
  for (const id of nodeIds) adj.set(id, []);
  for (const e of edges) adj.get(e.source)?.push(e.target);

  let best = 0;
  const memo = new Map<string, number>();

  function depth(node: string, visiting: Set<string>): number {
    if (memo.has(node)) return memo.get(node)!;
    if (visiting.has(node)) return 0;
    visiting.add(node);
    let maxChild = 0;
    for (const next of adj.get(node) ?? []) {
      maxChild = Math.max(maxChild, depth(next, visiting));
    }
    visiting.delete(node);
    const len = maxChild + 1;
    memo.set(node, len);
    return len;
  }

  for (const id of nodeIds) best = Math.max(best, depth(id, new Set()));
  return best;
}

function reachableFromRoots(nodeIds: Set<string>, edges: RecommendationEdge[]): Set<string> {
  const roots = [...nodeIds].filter((id) => !edges.some((e) => e.target === id));
  const adj = new Map<string, string[]>();
  for (const id of nodeIds) adj.set(id, []);
  for (const e of edges) adj.get(e.source)?.push(e.target);

  const seen = new Set<string>();
  const queue = [...roots];
  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const next of adj.get(cur) ?? []) queue.push(next);
  }
  return seen;
}

/** Validate graph structure — orphan roots, broken chains, unreachable nodes, critical path. */
export function reconcileGraph(
  graph: { nodes: RecommendationNode[]; edges: RecommendationEdge[] },
  opts: { criticalPathThreshold?: number } = {},
): GraphReconciliationFinding[] {
  const findings: GraphReconciliationFinding[] = [];
  const nodeIds = new Set(graph.nodes.map((n) => n.id));
  const edges = graph.edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));

  for (const e of graph.edges) {
    if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) {
      findings.push({
        kind: 'BROKEN_CHAIN',
        severity: 'block',
        message: `Edge references missing node: ${e.source} → ${e.target}`,
        edgeIds: [e.id],
      });
    }
  }

  const inDegree = new Map<string, number>();
  const outDegree = new Map<string, number>();
  for (const id of nodeIds) {
    inDegree.set(id, 0);
    outDegree.set(id, 0);
  }
  for (const e of edges) {
    inDegree.set(e.target, (inDegree.get(e.target) ?? 0) + 1);
    outDegree.set(e.source, (outDegree.get(e.source) ?? 0) + 1);
  }

  const hasEdges = edges.length > 0;

  for (const node of graph.nodes) {
    const indeg = inDegree.get(node.id) ?? 0;
    const outdeg = outDegree.get(node.id) ?? 0;
    // R-131-ROADMAP-FIX-B — only flag nodes with zero in/out edges
    if (indeg === 0 && outdeg === 0 && graph.nodes.length > 1) {
      findings.push({
        kind: 'ORPHAN_ROOT',
        severity: 'warn',
        message: `Truly isolated recommendation: "${node.canonicalForm.slice(0, 80)}"`,
        nodeIds: [node.id],
      });
    } else if (!hasEdges && indeg === 0 && phaseRank(node.phase) > phaseRank('immediate')) {
      findings.push({
        kind: 'ORPHAN_ROOT',
        severity: 'warn',
        message: `Later-phase item has no prerequisites: "${node.canonicalForm.slice(0, 80)}" (${node.phase})`,
        nodeIds: [node.id],
      });
    }

    const activeStatus =
      node.status === 'initiated' || node.status === 'pending-feedback';
    if (activeStatus) {
      const prereqIds = node.prerequisites?.length
        ? node.prerequisites
        : edges.filter((e) => e.target === node.id).map((e) => e.source);
      for (const prereqId of prereqIds) {
        const prereq = graph.nodes.find((n) => n.id === prereqId);
        if (prereq?.status === 'not-started') {
          findings.push({
            kind: 'BROKEN_CHAIN',
            severity: 'block',
            message: `Active item "${node.canonicalForm.slice(0, 60)}" depends on not-started "${prereq.canonicalForm.slice(0, 60)}"`,
            nodeIds: [prereqId, node.id],
          });
        }
      }
    }
  }

  const reachable = reachableFromRoots(nodeIds, edges);
  for (const node of graph.nodes) {
    if (!reachable.has(node.id)) {
      findings.push({
        kind: 'UNREACHABLE',
        severity: 'warn',
        message: `Recommendation not reachable from any root: "${node.canonicalForm.slice(0, 80)}"`,
        nodeIds: [node.id],
      });
    }
  }

  const pathLen = longestPathLength(nodeIds, edges);
  const threshold = opts.criticalPathThreshold ?? AUDIT_CONFIG.CRITICAL_PATH_THRESHOLD ?? 8;
  if (pathLen > threshold) {
    findings.push({
      kind: 'CRITICAL_PATH_LENGTH',
      severity: 'warn',
      message: `Longest dependency chain length ${pathLen} exceeds threshold ${threshold}`,
      pathLength: pathLen,
    });
  }

  return findings;
}

export async function buildRecommendationGraphFromReports(
  siteId: string,
  reports: SiteRagReport[],
  opts: {
    embedder?: ActionEmbedder;
    llmInferDeps?: LlmDepInferFn | null;
    skipSemantic?: boolean;
    statusByNodeId?: Record<string, RecommendationNode['status']>;
    statusByStableKey?: Record<string, RecommendationNode['status']>;
    extractOptions?: ExtractActionsOptions;
    /**
     * D5 — when supplied, the site's supply-chain plan is folded into the
     * roadmap: each awardable BoM line (open quote) becomes a procurement node
     * that explicitly UNBLOCKS its category's downstream construction
     * milestones. Previously `buildSupplyChainActionNodes` had ZERO callers
     * (#30) — the procurement → construction dependency chain never reached the
     * roadmap. Phase bucketing prefers each line's own `phaseId` COD via
     * `supplyChainPhases`.
     */
    supplyChainPlan?: import('../../types/supplyChain').SupplyChainPlan;
    /** D5 — project phases for per-line phase bucketing of SC action nodes. */
    supplyChainPhases?: SupplyChainActionPhaseLike[];
  } = {},
): Promise<RecommendationGraph> {
  // C10 — wrap the embedder once in a per-build cache so the same
  // canonicalForm is not re-embedded across extraction, explicit-edge, and
  // semantic-edge stages (which all embed `node.canonicalForm`).
  const baseEmbedder = opts.embedder ?? ((text: string) => embedText(text, { taskType: 'SEMANTIC_SIMILARITY' }));
  const embedder = memoizeEmbedder(baseEmbedder);
  const nodesRaw = await extractRecommendations(reports, embedder, undefined, opts.extractOptions);
  const reportNodes = nodesRaw.map((n) => ({
    ...n,
    status: opts.statusByStableKey
      ? opts.statusByStableKey[computeStableKey(n.canonicalForm)] ?? n.status
      : opts.statusByNodeId?.[n.id] ?? n.status,
  }));

  // D5 — fold in supply-chain procurement → construction action nodes/edges.
  // The SC nodes carry their own deterministic explicit edges; they are merged
  // with the report-derived nodes BEFORE dependency inference so cross-links
  // (e.g. a report action that depends on an energization milestone) can form.
  const scActions = opts.supplyChainPlan
    ? buildSupplyChainActionNodes(opts.supplyChainPlan, { phases: opts.supplyChainPhases })
    : { nodes: [], edges: [] };
  const scNodes = scActions.nodes.map((n) => ({
    ...n,
    status: opts.statusByNodeId?.[n.id] ?? n.status,
  }));
  const nodes = [...reportNodes, ...scNodes];

  const inferred = await inferDependencies(nodes, embedder, {
    llmInferDeps: opts.llmInferDeps,
    skipSemantic: opts.skipSemantic,
  });
  // SC explicit edges are authoritative procurement→construction links. SC node
  // ids (`scm-procure-*`, `*-downstream-*`) are disjoint from report cluster ids,
  // so these edges never collide with inferred ones — merge directly.
  const built = buildRecommendationGraph(nodes, [...inferred, ...scActions.edges]);
  const graph: RecommendationGraph = {
    siteId,
    nodes: built.nodes,
    edges: built.edges,
    findings: [...built.findings, ...reconcileGraph({ nodes: built.nodes, edges: built.edges })],
    builtAt: new Date().toISOString(),
    reportIds: reports.map((r) => r.id).filter(Boolean) as string[],
  };
  return graph;
}

export interface AppendRecommendationsOptions {
  llmInferDeps?: LlmDepInferFn | null;
  skipSemantic?: boolean;
  /**
   * Cosine-similarity threshold for treating a freshly-extracted node as a
   * duplicate of an existing node (in addition to exact stableKey match).
   * Defaults to the cluster threshold so dedupe is at least as strict as the
   * intra-build clustering that produced the existing nodes.
   */
  dedupeThreshold?: number;
  /** Override the embedder (tests). */
  embedder?: ActionEmbedder;
  /** Skip the LLM enrichment pass during extraction. Off in production
   *  (incremental updates enrich like a full rebuild); set true in unit tests
   *  to keep extraction fast + deterministic (no mocked-Gemini timeout). */
  skipLlmEnrichment?: boolean;
}

export interface AppendRecommendationsResult {
  graph: RecommendationGraph;
  /** Nodes genuinely appended (not duplicates of existing roadmap nodes). */
  addedNodes: RecommendationNode[];
  /** Count of freshly-extracted nodes dropped as duplicates of existing nodes. */
  duplicateCount: number;
}

/**
 * R-ROADMAP-INC — incremental roadmap update.
 *
 * Extracts + clusters ONLY the supplied `newReports`, then dedupes the result
 * against the existing graph's nodes by (1) exact `stableKey` match and
 * (2) cosine similarity of the canonical-text embedding. Genuinely-new nodes
 * are appended; existing nodes (and their user-set `status`) are preserved.
 * Dependency edges are re-inferred over the merged node set so that new nodes
 * link into the existing roadmap. Existing nodes keep their ids/stableKeys so
 * persisted status survives the merge.
 *
 * Note: extraction only touches the new reports, so per-report extraction cost
 * scales with the delta, not the whole corpus. Edge inference still runs over
 * the full merged set (cheap, deterministic for the non-LLM signals).
 */
export async function appendRecommendationsFromReports(
  existingGraph: RecommendationGraph,
  newReports: SiteRagReport[],
  embedder: ActionEmbedder,
  opts: AppendRecommendationsOptions = {},
): Promise<AppendRecommendationsResult> {
  const effectiveEmbedder = opts.embedder ?? embedder;
  const dedupeThreshold = opts.dedupeThreshold
    ?? AUDIT_CONFIG.RECOMMENDATION_CLUSTER_THRESHOLD
    ?? 0.8;

  // Backfill stableKey on existing nodes (pre-R-ROADMAP-INC graphs).
  const existingNodes = existingGraph.nodes.map(node => ({
    ...ensureStableKey(node),
    sourceReports: [...node.sourceReports],
  }));
  const existingIds = new Set(existingNodes.map((n) => n.id));

  // Extract + cluster ONLY the new reports.
  const extracted = (await extractRecommendations(newReports, effectiveEmbedder, undefined, {
    skipLlmEnrichment: opts.skipLlmEnrichment,
  }))
    .map(ensureStableKey);

  // Embed existing + candidate canonical text once for cosine dedupe.
  const existingVectors = await mapWithConcurrency(
    existingNodes,
    EMBED_CONCURRENCY,
    (n) => effectiveEmbedder(n.canonicalForm),
  );

  const addedNodes: RecommendationNode[] = [];
  let duplicateCount = 0;
  const mergeSources = (target: RecommendationNode, candidate: RecommendationNode): void => {
    const sourceKey = (source: RecommendationSourceRef) => JSON.stringify([source.reportId, source.reportType, source.sectionId]);
    const known = new Set(target.sourceReports.map(sourceKey));
    const added = candidate.sourceReports.filter(source => {
      const key = sourceKey(source);
      if (known.has(key)) return false;
      known.add(key);
      return true;
    });
    if (added.length === 0) return;
    target.sourceReports = [...target.sourceReports, ...added];
    target.memberCount += candidate.memberCount;
  };

  for (const candidate of extracted) {
    const key = candidate.stableKey;
    // (1) Exact content match against existing or already-added node.
    // Canonical equality also recognizes legacy cached keys minted before
    // citation normalization; a hash alone never proves task equality.
    const exact = [...existingNodes, ...addedNodes].find(node =>
      normalizeCanonicalText(node.canonicalForm) === normalizeCanonicalText(candidate.canonicalForm));
    if (exact) {
      mergeSources(exact, candidate);
      duplicateCount += 1;
      continue;
    }

    // (2) Near-duplicate by cosine similarity against existing nodes.
    const candidateVec = await effectiveEmbedder(candidate.canonicalForm);
    let duplicate: RecommendationNode | undefined;
    for (let i = 0; i < existingNodes.length; i += 1) {
      if (cosineSimilarity(candidateVec, existingVectors[i]) >= dedupeThreshold) {
        duplicate = existingNodes[i];
        break;
      }
    }
    if (!duplicate) {
      for (const added of addedNodes) {
        const addedVec = await effectiveEmbedder(added.canonicalForm);
        if (cosineSimilarity(candidateVec, addedVec) >= dedupeThreshold) {
          duplicate = added;
          break;
        }
      }
    }
    if (duplicate) {
      mergeSources(duplicate, candidate);
      duplicateCount += 1;
      continue;
    }

    // Genuinely new — give it a non-colliding id derived from its stableKey.
    let nodeId = key ?? candidate.id;
    if (existingIds.has(nodeId)) nodeId = `${nodeId}-${addedNodes.length}`;
    existingIds.add(nodeId);
    const appended: RecommendationNode = {
      ...candidate,
      id: nodeId,
      clusterId: nodeId,
    };
    addedNodes.push(appended);
  }

  const mergedNodes = [...existingNodes, ...addedNodes];

  // Re-infer edges over the merged set so new nodes link into the roadmap.
  const inferred = await inferDependencies(mergedNodes, effectiveEmbedder, {
    llmInferDeps: opts.llmInferDeps,
    skipSemantic: opts.skipSemantic,
  });
  const built = buildRecommendationGraph(mergedNodes, inferred);

  const mergedReportIds = [
    ...existingGraph.reportIds,
    ...newReports.map((r) => r.id).filter(Boolean) as string[],
  ].filter((id, i, arr) => arr.indexOf(id) === i);

  const graph: RecommendationGraph = {
    siteId: existingGraph.siteId,
    nodes: built.nodes,
    edges: built.edges,
    findings: [
      ...built.findings,
      ...reconcileGraph({ nodes: built.nodes, edges: built.edges }),
    ],
    builtAt: new Date().toISOString(),
    reportIds: mergedReportIds,
  };

  return { graph, addedNodes, duplicateCount };
}

/** Clear LLM pair cache (tests). */
export function clearRecommendationGraphCache(): void {
  LLM_PAIR_CACHE.clear();
}

export { PHASE_ORDER, phaseRank, parseDeadlineDays };

// ─────────────────────────────────────────────────────────────────────────────
// R-80 extension — SupplyChainAction nodes for procurement → construction deps
// ─────────────────────────────────────────────────────────────────────────────

export interface SupplyChainActionNode {
  nodeId: string;
  bomItemId: string;
  bomItemName: string;
  category: string;
  actionType: 'award' | 'reject';
}

export interface DownstreamAction {
  nodeId: string;
  label: string;
  weeksAfterAward: number;
}

const CATEGORY_DOWNSTREAM_MAP: Record<string, Array<{ label: string; weeksOffset: number }>> = {
  'power-conversion': [
    { label: 'Substation construction', weeksOffset: 8 },
    { label: 'Generator interconnect', weeksOffset: 12 },
    { label: 'Energization milestone', weeksOffset: 16 },
  ],
  'power-generation': [
    { label: 'Reactor building fitout', weeksOffset: 12 },
    { label: 'Steam system commissioning', weeksOffset: 20 },
    { label: 'Grid synchronization', weeksOffset: 24 },
  ],
  'cooling': [
    { label: 'Cooling tower erection', weeksOffset: 6 },
    { label: 'Thermal loop commissioning', weeksOffset: 10 },
  ],
  'energy-storage': [
    { label: 'BESS pad construction', weeksOffset: 4 },
    { label: 'Battery rack install', weeksOffset: 8 },
    { label: 'Grid-forming inverter sync', weeksOffset: 12 },
  ],
  'data-center-it': [
    { label: 'Rack and PDU install', weeksOffset: 4 },
    { label: 'Network cabling', weeksOffset: 6 },
    { label: 'Operational handover', weeksOffset: 10 },
  ],
  'civil-construction': [
    { label: 'Foundation completion', weeksOffset: 6 },
    { label: 'Structural steel erection', weeksOffset: 12 },
  ],
};

/**
 * Build downstream action nodes for a BoM line based on its category.
 * Used by the Compare panel to show "Award unblocks N downstream actions".
 */
export function getDownstreamActions(
  bomItemId: string,
  category: string,
): DownstreamAction[] {
  const templates = CATEGORY_DOWNSTREAM_MAP[category];
  if (!templates) return [];
  return templates.map((t, i) => ({
    nodeId: `${bomItemId}-downstream-${i}`,
    label: t.label,
    weeksAfterAward: t.weeksOffset,
  }));
}

export interface SupplyChainActionPhaseLike {
  id: string;
  sequence: number;
  targetInServiceDate: string | null;
}

export interface BuildSupplyChainActionNodesOptions {
  /**
   * D5 — project phases (from `effectivePhases(intent).phases`). When the BoM
   * line carries a `phaseId` that resolves here, the procurement + downstream
   * nodes are bucketed against THAT phase's COD instead of the flat
   * "immediate / +Nw" heuristic. This is the "prefer bomItem.phaseId over the
   * nearest-COD heuristic" wiring (#30): a Phase-4 award is not surfaced as an
   * immediate action when its phase COD is years out.
   */
  phases?: SupplyChainActionPhaseLike[];
  /** Reference "now" for date→phase-bucket math. Defaults to current time. */
  now?: Date | string;
}

/** Approx milliseconds per ISO week — only used to slide a downstream action
 *  forward from its phase-COD anchor, never for display. */
const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/**
 * Build SupplyChainAction graph nodes from a site's supply chain plan.
 * Each awardable BoM line gets a node; edges connect to downstream
 * construction actions based on the equipment category.
 *
 * D5 — when `opts.phases` is supplied and a line carries a resolvable
 * `phaseId`, the procurement node is bucketed against that phase's COD (via the
 * shared `bucketPhase`) and each downstream action is bucketed against the
 * phase COD pushed forward by its weeks-offset. Lines with no resolvable phase
 * fall back to the legacy flat heuristic (award = immediate; downstream =
 * 30d/90d by weeks-offset), so single-phase / untagged plans are unchanged.
 */
export function buildSupplyChainActionNodes(
  plan: import('../../types/supplyChain').SupplyChainPlan,
  opts: BuildSupplyChainActionNodesOptions = {},
): {
  nodes: RecommendationNode[];
  edges: RecommendationEdge[];
} {
  const nodes: RecommendationNode[] = [];
  const edges: RecommendationEdge[] = [];
  const refDate = opts.now ? new Date(opts.now) : new Date();
  const phaseById = new Map<string, SupplyChainActionPhaseLike>();
  for (const p of opts.phases ?? []) phaseById.set(p.id, p);

  for (const bomItem of plan.bom) {
    const hasQuotes = plan.quotes.some(
      q => q.bomItemId === bomItem.id && q.status === 'open',
    );
    if (!hasQuotes) continue;

    // Prefer the line's OWN phase COD over the flat "immediate" default.
    const phase = bomItem.phaseId ? phaseById.get(bomItem.phaseId) : undefined;
    const phaseCod = phase?.targetInServiceDate ?? null;
    const procurementPhase: RecommendationPhase = phaseCod
      ? bucketPhase(phaseCod, refDate)
      : 'immediate';

    const procurementNodeId = `scm-procure-${bomItem.id}`;
    nodes.push({
      id: procurementNodeId,
      canonicalForm: `Award quote for ${bomItem.name}`,
      text: `Award quote for ${bomItem.name} (${bomItem.category})`,
      phase: procurementPhase,
      status: bomItem.awardedSupplierId ? 'completed' : 'not-started',
      sourceReports: [],
      clusterId: procurementNodeId,
      memberCount: 1,
    });

    const downstream = getDownstreamActions(bomItem.id, bomItem.category);
    for (const action of downstream) {
      // When we know the phase COD, anchor the downstream action to it +offset
      // and bucket from there; otherwise keep the legacy weeks-offset heuristic.
      let downstreamPhase: RecommendationPhase;
      if (phaseCod) {
        const codMs = Date.parse(phaseCod);
        downstreamPhase = Number.isFinite(codMs)
          ? bucketPhase(new Date(codMs + action.weeksAfterAward * MS_PER_WEEK).toISOString(), refDate)
          : (action.weeksAfterAward <= 8 ? '30d' : '90d');
      } else {
        downstreamPhase = action.weeksAfterAward <= 8 ? '30d' : '90d';
      }
      nodes.push({
        id: action.nodeId,
        canonicalForm: action.label,
        text: `${action.label} (T+${action.weeksAfterAward}w after ${bomItem.name} award)`,
        phase: downstreamPhase,
        status: 'not-started',
        sourceReports: [],
        clusterId: action.nodeId,
        memberCount: 1,
      });
      edges.push({
        id: `${procurementNodeId}->${action.nodeId}:explicit`,
        source: procurementNodeId,
        target: action.nodeId,
        type: 'explicit',
        confidence: 0.9,
        reason: `${bomItem.name} procurement enables ${action.label}`,
      });
    }
  }

  return { nodes, edges };
}


/** Top-N longest dependency paths (for CLI / audit summary). */
export function topCriticalPaths(
  graph: Pick<RecommendationGraph, 'nodes' | 'edges'>,
  limit = 3,
): Array<{ length: number; nodeIds: string[]; labels: string[] }> {
  const nodeIds = new Set(graph.nodes.map((n) => n.id));
  const adj = new Map<string, string[]>();
  const labelById = new Map(graph.nodes.map((n) => [n.id, n.canonicalForm]));
  for (const id of nodeIds) adj.set(id, []);
  for (const e of graph.edges) {
    if (nodeIds.has(e.source) && nodeIds.has(e.target)) {
      adj.get(e.source)?.push(e.target);
    }
  }

  const paths: Array<{ length: number; nodeIds: string[] }> = [];
  const roots = [...nodeIds].filter((id) => !graph.edges.some((e) => e.target === id));

  function dfs(node: string, path: string[], visiting: Set<string>) {
    if (visiting.has(node)) return;
    visiting.add(node);
    const nextPath = [...path, node];
    const children = adj.get(node) ?? [];
    if (children.length === 0) {
      paths.push({ length: nextPath.length, nodeIds: nextPath });
    } else {
      for (const child of children) dfs(child, nextPath, visiting);
    }
    visiting.delete(node);
  }

  for (const root of roots.length > 0 ? roots : [...nodeIds]) {
    dfs(root, [], new Set());
  }

  paths.sort((a, b) => b.length - a.length);
  return paths.slice(0, limit).map((p) => ({
    ...p,
    labels: p.nodeIds.map((id) => labelById.get(id) ?? id),
  }));
}
